/* eslint-disable @typescript-eslint/no-explicit-any */
import DrawCenter from '@/components/DrawCenter/DrawCenter'
import {
  FormButton,
  FormGroup,
  FormInput,
  FormTextRequire,
} from '@/components/Form'
import { AddToCartType } from '@/constants'
import { useCart, useNotification } from '@/containers'
import { IconCheckedSuccess } from '@/styles/Icon'
import type { Product, ProductDetail } from '@/types'
import { translateSchema } from '@/utils/validations'
import { Dialog } from '@headlessui/react'
import clsx from 'clsx'
import { Form, Formik } from 'formik'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import AddToCartSuccess from '../AddToCartSuccess/AddToCartSuccess'
import Button from '../Button'
import AddToCartIcon from '../Icon/AddToCartIcon'
import ImageOptimize from '../ImageOptimize'

interface Props {
  handleAddToCart: (
    product: Product | ProductDetail,
    type: string
  ) => Promise<any>
  entrySummaryData: ProductDetail
  isOutOfStock: boolean
}

const ProductActions = ({
  handleAddToCart,
  entrySummaryData,
  isOutOfStock,
}: Props) => {
  const [isOpenContact, setIsOpenContact] = useState(false)
  const { systemConfig } = useNotification()
  const { sendContact } = useCart()
  const t = useTranslations()

  const [openAddToCartModal, setOpenAddToCartModal] = useState(false)

  const isPreOrder = !!entrySummaryData.preOrder
  const allowInstallment = !!entrySummaryData.allowInstallment && !isPreOrder

  useEffect(() => {
    if (openAddToCartModal) {
      const timeout = setTimeout(() => {
        setOpenAddToCartModal(false)
      }, 5000)

      return () => clearTimeout(timeout)
    }
  }, [openAddToCartModal])

  return (
    <div>
      {openAddToCartModal && (
        <Dialog
          open={openAddToCartModal}
          onClose={() => setOpenAddToCartModal(false)}
        >
          <AddToCartSuccess />
        </Dialog>
      )}
      <div className='flex w-full items-center justify-center gap-3 py-3'>
        {isOutOfStock && (
          <Button
            color='secondary'
            variant='outlined'
            size='large'
            className='!text-base !font-bold'
            onClick={() => setIsOpenContact(true)}
          >
            Gọi lại cho tôi
          </Button>
        )}

        {!isOutOfStock && (
          <>
            <Button
              className='flex !w-[58px] !min-w-0 items-center justify-center !border-2 !p-0 !text-base !font-bold'
              color='secondary'
              variant='outlined'
              size='large'
              onClick={() =>
                handleAddToCart(entrySummaryData, AddToCartType.ADD).then(() =>
                  setOpenAddToCartModal(true)
                )
              }
            >
              <AddToCartIcon />
            </Button>
            <Button
              className='grow !text-base !font-bold'
              size='large'
              color='secondary'
              onClick={() =>
                handleAddToCart(entrySummaryData, AddToCartType.BUY_NOW)
              }
            >
              <span>Mua ngay</span>
            </Button>
            {isPreOrder && (
              <Button
                className='grow !text-base !font-bold'
                color='primary'
                size='large'
                onClick={() =>
                  handleAddToCart(entrySummaryData, AddToCartType.PRE_ORDER)
                }
              >
                Đặt trước
              </Button>
            )}
            {allowInstallment && (
              <Button
                className='grow !text-base !font-bold'
                color='primary'
                size='large'
                onClick={() =>
                  handleAddToCart(entrySummaryData, AddToCartType.INSTALLMENT)
                }
              >
                Trả góp
              </Button>
            )}
          </>
        )}
      </div>

      <div className='text-black font-sm mt-2 text-center font-semibold'>
        <ImageOptimize
          src='/assets/icons/phone.svg'
          alt='phone'
          className='mr-1 inline-block h-[15px] w-[15px]'
        />
        Gọi{' '}
        {!!systemConfig?.companyPhone && (
          <a
            href={`tel:${systemConfig?.companyPhone}`}
            className='inline-block text-secondary'
          >
            {systemConfig?.companyPhone}
          </a>
        )}{' '}
        {!!systemConfig?.companyPhone2 && '- '}
        {!!systemConfig?.companyPhone2 && (
          <a
            href={`tel:${systemConfig?.companyPhone2}`}
            className='inline-block text-secondary'
          >
            {systemConfig?.companyPhone2}
          </a>
        )}{' '}
        để được tư vấn mua hàng
      </div>

      <DrawCenter
        open={isOpenContact || false}
        onClose={() => setIsOpenContact(false)}
        title={'Chọn trung tâm'}
      >
        <div className='listLocation flex h-full flex-row flex-wrap content-start  justify-start gap-y-[12px] border-gray-100 pb-10 '>
          <Formik
            initialValues={{
              phone: '',
              name: '',
            }}
            validationSchema={translateSchema('contact', t)}
            onSubmit={async values => {
              await sendContact({
                ...values,
                productIds: [entrySummaryData?._id],
              }).then(() => {
                toast.success('Gửi yêu cầu thành công', {
                  position: 'bottom-center',
                  icon: <IconCheckedSuccess />,
                  autoClose: 1500,
                  hideProgressBar: true,
                  closeOnClick: true,
                  pauseOnHover: false,
                  draggable: true,
                  progress: undefined,
                  theme: 'dark',
                })
              })
              setIsOpenContact(false)
              return
            }}
          >
            {props => (
              <Form onSubmit={props.handleSubmit}>
                <span className='text-mainBody italic text-dark-400'>
                  Vui lòng nhập các thông tin phía dưới để{' '}
                  {systemConfig?.websiteBrandName} có thể liên hệ, tư vấn cho
                  quý khách hàng một cách nhanh và thuần lợi nhất.
                </span>
                <div className='mt-[20px]'>
                  <FormGroup className='mb-5 flex w-full flex-col'>
                    <FormInput
                      name='name'
                      placeholder='Họ và tên'
                      value={props.values.name}
                      onChange={props.handleChange}
                      alert
                      className={clsx(
                        props.errors.name && 'border border-orange',
                        'border-border-500 bg-white-500 rounded border text-base transition hover:cursor-pointer focus:border-blue disabled:bg-gray-200 disabled:text-dark-400/50'
                      )}
                    ></FormInput>
                    {props.errors.name && (
                      <FormTextRequire className=' text-sm font-normal text-orange'>
                        {props.errors.name as string}
                      </FormTextRequire>
                    )}
                  </FormGroup>
                  <FormGroup className='mb-5 flex w-full flex-col'>
                    <FormInput
                      name='phone'
                      placeholder='Nhập số điện thoại'
                      value={props.values.phone}
                      onChange={e => {
                        props.handleChange({
                          target: {
                            value: e.target.value.trim().replace(/\D/, ''),
                            name: 'phone',
                          },
                        })
                      }}
                      alert
                      className={`border-border-500 bg-white-500 rounded border text-base transition hover:cursor-pointer focus:border-blue disabled:bg-gray-200 disabled:text-dark-400/50`}
                    />
                    {props.errors.phone && (
                      <FormTextRequire className=' text-sm font-normal text-orange'>
                        {props.errors.phone as string}
                      </FormTextRequire>
                    )}
                  </FormGroup>
                  <FormButton
                    className={`h-[54px] w-full overflow-hidden rounded-[100px] bg-orange text-fieldLabel font-bold text-white outline-none`}
                  >
                    Xác nhận
                  </FormButton>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </DrawCenter>
    </div>
  )
}
export default ProductActions
